import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, CircularProgress } from '@mui/material';

const ExternalEffortModal = ({ open, onClose, selectedCompany, formData, onSubmit, isSubmitting, isEditMode = false }) => {
  // Local state to avoid re-rendering parent on every keystroke
  const [localFormData, setLocalFormData] = useState(formData);

  // Sync local state when modal opens or formData changes from parent
  useEffect(() => {
    if (open) {
      setLocalFormData(formData);
    }
  }, [open, formData]);

  const handleLocalChange = (field, value) => {
    setLocalFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const handleSubmit = () => {
    onSubmit(localFormData);
  };

  const isFormValid = localFormData.outlet.trim() && localFormData.title.trim() && localFormData.date;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEditMode ? 'Edit additional effort' : 'Add additional effort'} - {selectedCompany}
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Outlet"
            fullWidth
            required
            value={localFormData.outlet}
            onChange={(e) => handleLocalChange('outlet', e.target.value)}
            placeholder="e.g., TechCrunch, Forbes, CNN"
          />

          <TextField
            label="Title"
            fullWidth
            required
            multiline
            rows={2}
            value={localFormData.title}
            onChange={(e) => handleLocalChange('title', e.target.value)}
            placeholder="Enter the article/coverage title"
          />

          <TextField
            label="Date"
            type="date"
            fullWidth
            required
            value={localFormData.date}
            onChange={(e) => handleLocalChange('date', e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Status"
            fullWidth
            select
            value={localFormData.status}
            onChange={(e) => handleLocalChange('status', e.target.value)}
            SelectProps={{ native: true }}
          >
            <option value="submitted to journalist">Proactive outreach</option>
            <option value="coordinating next steps">Coordinating next steps</option>
            <option value="pending publication">Pending publication</option>
            <option value="successful placement">Successful placement</option>
            <option value="client passed">Client passed</option>
            <option value="journalist passed">Journalist passed</option>
            <option value="insights not included">Insights not included</option>
            <option value="paid editorial">Paid editorial</option>
            <option value="repurpose">Repurpose</option>
            <option value="successful">Successful (Legacy)</option>
          </TextField>

          <TextField
            label="Link to Publication"
            fullWidth
            value={localFormData.link}
            onChange={(e) => handleLocalChange('link', e.target.value)}
            placeholder="https://..."
            helperText="Optional: Link to the published article or coverage"
          />

          <TextField
            label="Outlet Homepage URL"
            fullWidth
            value={localFormData.outletLink}
            onChange={(e) => handleLocalChange('outletLink', e.target.value)}
            placeholder="https://techcrunch.com"
            helperText="Optional: Main website URL for outlet logo/favicon"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting || !isFormValid}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
        >
          {isSubmitting ? (isEditMode ? 'Updating...' : 'Adding...') : isEditMode ? 'Update additional effort' : 'Add additional effort'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ExternalEffortModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedCompany: PropTypes.string.isRequired,
  formData: PropTypes.shape({
    outlet: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    date: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    link: PropTypes.string,
    outletLink: PropTypes.string,
  }).isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  isEditMode: PropTypes.bool,
};

export default ExternalEffortModal;
